import { z } from 'zod';
import { CampaignAttributionSchema } from '../campaign-attribution.js';
import { createPaginatedResponseSchema, saPhoneNumberSchema } from './common.js';
// =============================================================================
// User
// =============================================================================
export const UserProfileSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    phoneNumber: z.string().nullable(),
    phoneVerifiedAt: z.string().nullable(),
    image: z.string().nullable().optional(),
    createdAt: z.string(),
    isSuperAdmin: z.boolean().optional(),
    hasPassword: z.boolean().optional(),
    emailVerified: z.string().nullable().optional(),
});
export const UpdateUserProfileInputSchema = z.object({
    name: z.string().min(1, 'Name is required').max(200, 'Name is too long'),
});
// =============================================================================
// Phone Verification
// =============================================================================
export const SendPhoneCodeInputSchema = z.object({
    phoneNumber: saPhoneNumberSchema,
});
export const SendPhoneCodeResponseSchema = z.object({
    cooldownSeconds: z.number(),
});
export const VerifyPhoneCodeInputSchema = z.object({
    code: z.string().length(6).regex(/^\d{6}$/, 'Code must be 6 digits'),
});
export const VerifyPhoneCodeResponseSchema = z.object({
    verified: z.boolean(),
    attemptsRemaining: z.number().optional(),
});
// =============================================================================
// Auth
// =============================================================================
export const RegisterInputSchema = z.object({
    email: z.email(),
    password: z.string().min(12, 'Password must be at least 12 characters.').max(128),
    name: z.string().trim().min(1).max(200),
    accountName: z.string().trim().min(1).max(100).optional(),
    idempotencyKey: z.string().min(1),
    attribution: CampaignAttributionSchema.optional(),
});
// POST /auth/register returns { success: true } only. No JWT, no UserProfile.
// The User row is not created at register time; it materialises when the
// verification link is clicked. See verifyAndMaterialise in auth.service.ts.
export const RegisterResponseSchema = z.object({
    success: z.boolean(),
});
export const LoginInputSchema = z.object({
    email: z.email(),
    password: z.string().min(1).max(128),
});
export const AuthResponseSchema = z.object({
    token: z.string(),
    user: UserProfileSchema,
    accountId: z.string().optional(),
});
export const ForgotPasswordInputSchema = z.object({
    email: z.string().email(),
});
export const ForgotPasswordResponseSchema = z.object({
    success: z.boolean(),
});
export const ResetPasswordInputSchema = z.object({
    token: z.string().min(1).max(256),
    newPassword: z.string().min(12, 'Password must be at least 12 characters.').max(128),
});
export const ResetPasswordResponseSchema = z.object({
    success: z.boolean(),
    // 'setup' = first-time password add (typically an OAuth user adding
    // email+password as a sign-in method while authenticated). 'reset' = an
    // existing password was replaced via the forgot-password flow. The frontend
    // uses this to decide whether to keep the user signed in (setup) or send
    // them to /login (reset bumps tokenVersion, invalidating their JWT).
    mode: z.enum(['setup', 'reset']),
    // Returned so the frontend can pre-fill the email login form after a
    // successful reset, sparing the user from retyping their email.
    email: z.string().email(),
});
export const ChangePasswordInputSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(12, 'Password must be at least 12 characters.').max(128),
});
export const ChangePasswordResponseSchema = z.object({
    token: z.string(),
});
// `password` must match the one on the pending registration. Register is
// unauthenticated, so anyone can put a password on an address's pending row; only
// the inbox owner receives the token. Requiring both in one request is what stops a
// password chosen by someone else becoming the credentials of the verified user.
export const VerifyEmailInputSchema = z.object({
    token: z.string().min(1).max(256),
    password: z.string().min(1).max(128),
});
// POST /auth/verify-email materialises the User row and returns { success, email }.
// No JWT is issued here; the caller follows up with POST /auth/login using the
// email returned and the password it just confirmed.
export const VerifyEmailResponseSchema = z.object({
    success: z.boolean(),
    email: z.string().email(),
});
// Under the pending-registration invariant, the resend-verification flow no
// longer needs a password: there is no User row to bcrypt against. The body
// shape is now { email } only.
export const ResendVerificationInputSchema = z.object({
    email: z.string().email(),
});
export const ResendVerificationResponseSchema = z.object({
    success: z.boolean(),
});
export const E2ETokenInputSchema = z.object({
    name: z.string().optional(),
    email: z.email().optional(),
});
export const E2EActivateSubscriptionInputSchema = z.object({
    accountId: z.string(),
    planId: z.string().optional(), // defaults to pro_v2
    targetStatus: z.enum(['PENDING_PAYMENT_CONFIRMATION', 'ACTIVE', 'EXPIRED', 'PAST_DUE']).optional(), // defaults to ACTIVE
});
// =============================================================================
// OAuth
// =============================================================================
export const OAuthProviderSchema = z.enum(['google', 'microsoft']);
/**
 * `context.reason` on the 403 an OAuth sign-in gets when the provider did not
 * verify the email, so the callback page can offer the email-and-password route
 * instead of a generic failure.
 */
export const OAUTH_EMAIL_UNVERIFIED_REASON = 'provider-email-unverified';
/**
 * `context.reason` on the 400 an email or calendar connect gets when the user
 * declined a required permission on the consent screen, so the client can tell
 * a refused permission from any other failure.
 */
export const OAUTH_SCOPES_NOT_GRANTED_REASON = 'oauth-scopes-not-granted';
/**
 * Provider for an inbound email connection. Superset of `OAuthProvider`
 * because IMAP is not OAuth-based.
 *
 * See `apps/backend/platform/src/domains/email-inbox/README.md` (IMAP section) for connect flow, backfill, polling, and failure modes.
 */
export const EmailProviderSchema = z.enum(['google', 'microsoft', 'imap']);
export const OAuthAuthorizeQuerySchema = z.object({
    frontendUrl: z.url(),
    invitedEmail: z.email().optional(),
});
export const OAuthAuthorizeResponseSchema = z.object({
    authorizationUrl: z.url(),
});
export const OAuthCallbackBodySchema = z.object({
    code: z.string(),
    state: z.string(),
});
/** Sign-in callback body. `attribution` is recorded only when the callback creates a user. */
export const OAuthSignInCallbackBodySchema = OAuthCallbackBodySchema.extend({
    attribution: CampaignAttributionSchema.optional(),
});
export const OAuthCallbackResponseSchema = z.object({
    token: z.string(),
    user: z.object({
        id: z.string(),
        email: z.string(),
        name: z.string(),
    }),
    isNewUser: z.boolean(),
    linkedExisting: z.boolean().optional(),
    accountId: z.string().optional(),
});
export const OAuthUnlinkResponseSchema = z.object({
    success: z.boolean(),
});
export const LinkedAccountSchema = z.object({
    provider: OAuthProviderSchema,
    // ISO 8601 string (JSON-serialised from a backend Date). The previous
    // z.date() declaration silently broke once the user had any connected
    // OAuth account: the response always carries an ISO string over the wire,
    // and the client-side parse failed.
    linkedAt: z.string(),
    scope: z.string().nullable(),
});
export const OAuthAccountListResponseSchema = createPaginatedResponseSchema(LinkedAccountSchema);
// =============================================================================
// Auth - Account Selection
// =============================================================================
export const SelectAccountInputSchema = z.object({
    accountId: z.string(),
});
export const SelectAccountResponseSchema = z.object({
    token: z.string(),
});
// =============================================================================
// Auth - Logout
// =============================================================================
/**
 * POST /auth/logout takes no body. Exposed as a schema so the route validates
 * the empty body shape rather than accepting arbitrary payloads.
 */
export const LogoutInputSchema = z.object({});
export const LogoutResponseSchema = z.object({
    success: z.boolean(),
});
